import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { Adg4Button } from '@/components/adg4/button/src/button';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { Bundle } from '@/utils/urls';

import { useCloudStatus } from '../../hooks/use-cloud-status';
import { JiraOnboardingContainer } from '../jira/jira-onboarding-container/jira-onboarding-container';

import { CoffeeSvg } from './assets/coffee-svg';
import { ErrorSvg } from './assets/error-svg';

const mainContainer = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	height: '100%',
});

const preHeaderContainer = css({
	display: 'flex',
	justifyContent: 'center',
	padding: '16px 0px 32px 0px',
});

const support = css({
	fontFamily: wacToken('font.charlie.text'),
});

const link = css({
	textDecoration: 'underline',
	color: wacToken('color.primary.blue'),
});

const UnRecoverablePreheader = () => {
	return (
		<div css={preHeaderContainer}>
			<ErrorSvg />
		</div>
	);
};

const RecoverablePreheader = () => {
	return (
		<div css={preHeaderContainer}>
			<CoffeeSvg />
		</div>
	);
};

type AsyncArchitectureErrorViewProps = {
	errorType: 'un-recoverable' | 'recoverable';
};

export const AsyncArchitectureErrorView = ({ errorType }: AsyncArchitectureErrorViewProps) => {
	const { isProduction } = useCloudStatus();
	const intl = useIntl();

	const isRecoverable = errorType === 'recoverable';
	const homeUrl = isProduction ? 'https://home.atlassian.com' : 'https://home.stg.atlassian.com';

	const params = new URLSearchParams();
	params.append('bundle', 'jira-service-management' as Bundle);
	const signupUrl = useLocalizedHref(`/try/cloud/signup?${params}`);

	// conditionally define based on error type
	const PreHeader = isRecoverable ? RecoverablePreheader : UnRecoverablePreheader;

	const url = isRecoverable ? homeUrl : signupUrl;

	// header text
	const unrecoverableHeaderText = intl.formatMessage({
		id: 'asyncArch.errorMessage.unRecoverable.header',
		defaultMessage: 'Something went wrong',
	});
	const recoverableHeaderText = intl.formatMessage({
		id: 'asyncArch.errorMessage.recoverable.header',
		defaultMessage: 'Your site needs a little extra time',
	});

	// subheader text
	const recoverableSubheaderText = intl.formatMessage({
		id: 'asyncArch.errorMessage.recoverable.subheader',
		defaultMessage: 'Grab a cup of coffee while we prepare your site.',
	});

	// cta text
	const unrecoverableCtaText = intl.formatMessage({
		id: 'asyncArch.errorMessage.unRecoverable.cta',
		defaultMessage: 'Try signing up again',
	});
	const recoverableCtaText = intl.formatMessage({
		id: 'asyncArch.errorMessage.recoverable.cta',
		defaultMessage: 'Take me home',
	});

	// support text
	const persistText = intl.formatMessage({
		id: 'asyncArch.errorMessage.persist',
		defaultMessage: 'If this persists, please ',
	});
	const supportText = intl.formatMessage({
		id: 'asyncArch.errorMessage.support',
		defaultMessage: 'contact support',
	});

	return (
		<div css={mainContainer}>
			<JiraOnboardingContainer
				header={isRecoverable ? recoverableHeaderText : unrecoverableHeaderText}
				subheader={recoverableSubheaderText}
				PreHeader={PreHeader}
				hideSubHeader={!isRecoverable}
			>
				<Adg4Button
					onClick={() => {
						window.location.assign(url);
					}}
				>
					{isRecoverable ? recoverableCtaText : unrecoverableCtaText}
				</Adg4Button>
				{!isRecoverable && (
					<p css={support}>
						{persistText}
						<a css={link} href="https://support.atlassian.com/contact/">
							{supportText}
						</a>
					</p>
				)}
			</JiraOnboardingContainer>
		</div>
	);
};
